import { FadeIn } from './FadeIn';
import { NavBar } from './NavBar';
import { useLanguage } from './LanguageContext';
import founderImage from './media/images/img.png';

export function FounderPage() {
  const { messages, t, language, toggleLanguage } = useLanguage();
  const f = messages.founderPage;

  return (
    <>
      <NavBar t={t} language={language} toggleLanguage={toggleLanguage} />
      <main className="bg-surface-container-lowest min-h-screen pb-20">
        {/* Hero */}
        <section className="relative isolate overflow-hidden bg-primary">
          <div className="absolute inset-0" aria-hidden>
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2400&q=80"
              alt=""
              className="w-full h-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/85 to-primary" />
            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-secondary/25 rounded-full blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-secondary/15 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-8 pt-36 pb-24 lg:pb-32">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
              <FadeIn className="lg:col-span-3 space-y-6" mode="mount">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white/85 text-sm font-semibold uppercase tracking-[0.25em]">
                  <span className="material-symbols-outlined text-secondary text-base" aria-hidden="true">
                    verified
                  </span>
                  {f.kicker}
                </span>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-headline font-extrabold text-white leading-[1.05] tracking-tight">
                  {f.name}
                </h1>
                <p className="text-xl sm:text-2xl text-secondary font-semibold leading-snug max-w-2xl">
                  {f.role}
                </p>
                <div className="pt-2 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-secondary/15 border border-secondary/40 backdrop-blur">
                  <span className="material-symbols-outlined text-secondary text-2xl" aria-hidden="true">
                    workspace_premium
                  </span>
                  <span className="text-white font-bold">{f.experienceChip}</span>
                </div>
              </FadeIn>
              <FadeIn className="lg:col-span-2 flex justify-center lg:justify-end" delay={150} mode="mount">
                <div className="relative">
                  <div className="absolute inset-0 bg-secondary/30 blur-3xl rounded-full" aria-hidden />
                  <div className="relative w-[260px] h-[320px] sm:w-[320px] sm:h-[400px] rounded-[2.5rem] overflow-hidden border-[6px] border-white/15 shadow-2xl shadow-black/40">
                    <img src={founderImage} alt={f.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent" />
                  </div>
                  <div className="absolute -bottom-5 -left-5 rtl:-right-5 rtl:left-auto w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center shadow-xl shadow-secondary/40">
                    <span className="material-symbols-outlined text-white text-4xl" aria-hidden="true">
                      auto_awesome
                    </span>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Professional Profile */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-2 space-y-4">
                <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                  {language === 'ar' ? 'لمحة عن' : 'About'}
                </span>
                <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-primary tracking-tight leading-tight">
                  {f.profile.title}
                </h2>
                <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {f.profile.highlights.map((h) => (
                    <div
                      key={h.label}
                      className="flex items-center gap-3 rounded-xl bg-surface-container-low border border-outline-variant/30 p-3"
                    >
                      <div className="shrink-0 w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                        <span className="material-symbols-outlined text-secondary text-xl" aria-hidden="true">
                          {h.icon}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-on-surface">{h.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-3">
                <div className="relative pl-8 rtl:pr-8 rtl:pl-0 border-l-4 rtl:border-r-4 rtl:border-l-0 border-secondary/70">
                  {/* <span className="material-symbols-outlined block text-secondary text-5xl leading-none mb-4" aria-hidden="true">
                    format_quote
                  </span> */}
                  <p className="text-lg sm:text-xl leading-relaxed text-on-surface-variant font-medium">
                    {f.profile.body}
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* Key Milestones — Timeline */}
        <section className="relative isolate bg-primary py-20 sm:py-28 overflow-hidden">
          <div className="absolute inset-0 opacity-20" aria-hidden>
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary via-primary/95 to-primary" />
          </div>
          <div className="absolute top-1/3 right-0 rtl:left-0 rtl:right-auto w-[500px] h-[500px] bg-secondary/10 rounded-full blur-3xl" aria-hidden />
          <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-8">
            <FadeIn>
              <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
                <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                  {language === 'ar' ? 'المسيرة' : 'Career'}
                </span>
                <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-white tracking-tight">
                  {f.milestones.title}
                </h2>
                <p className="text-white/75 text-lg">{f.milestones.subtitle}</p>
              </div>
            </FadeIn>

            <div className="relative">
              {/* Vertical timeline line */}
              <div
                className="hidden md:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-secondary/40 to-transparent"
                aria-hidden
              />
              <div className="space-y-12">
                {f.milestones.items.map((item, idx) => {
                  const isEven = idx % 2 === 0;
                  return (
                    <FadeIn key={item.period} delay={idx * 100}>
                      <div className="md:grid md:grid-cols-2 md:gap-10 items-center relative">
                        <div className={`${isEven ? 'md:pr-10 rtl:md:pl-10 rtl:md:pr-0' : 'md:col-start-2 md:pl-10 rtl:md:pr-10 rtl:md:pl-0'}`}>
                          <article className="rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 hover:border-secondary/50 transition-colors p-7 sm:p-8">
                            <div className="flex items-start gap-5 mb-5">
                              <div className="shrink-0 w-14 h-14 rounded-2xl bg-secondary/15 border border-secondary/40 flex items-center justify-center">
                                <span className="material-symbols-outlined text-secondary text-3xl" aria-hidden="true">
                                  {item.icon}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <span className="block text-sm font-bold text-secondary tracking-wide">
                                  {String(idx + 1).padStart(2, '0')} — {item.period}
                                </span>
                                <h3 className="mt-1 text-xl sm:text-2xl font-headline font-bold text-white leading-snug">
                                  {item.role}
                                </h3>
                              </div>
                            </div>
                            <ul className="space-y-3">
                              {item.points.map((p) => (
                                <li key={p} className="flex gap-3 items-start text-white/80">
                                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-secondary" aria-hidden />
                                  <span className="leading-relaxed">{p}</span>
                                </li>
                              ))}
                            </ul>
                          </article>
                        </div>

                        {/* Timeline node */}
                        <div
                          className="hidden md:flex absolute top-8 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-secondary ring-4 ring-primary items-center justify-center shadow-lg shadow-secondary/50 z-10"
                          aria-hidden
                        />
                      </div>
                    </FadeIn>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Featured video + external links: EN = text left / video right; AR = video left / text right (lg) */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16 space-y-4">
              <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                {f.mediaSpotlight.kicker}
              </span>
              <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-primary tracking-tight">
                {f.mediaSpotlight.title}
              </h2>
              <p className="text-on-surface-variant text-lg leading-relaxed">{f.mediaSpotlight.subtitle}</p>
            </div>
          </FadeIn>
          <FadeIn delay={80}>
            <div
              className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-[auto,auto] gap-10 lg:gap-x-14 gap-y-6"
              dir="ltr"
            >
              <div
                className={`w-full min-w-0 order-1 lg:order-none lg:row-start-2 lg:self-stretch ${
                  language === 'en' ? 'lg:col-start-2' : 'lg:col-start-1'
                }`}
              >
                <div className="relative w-full aspect-video rounded-3xl overflow-hidden border border-outline-variant/30 bg-on-surface/5 shadow-2xl shadow-primary/5">
                  <iframe
                    title="YouTube"
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${f.mediaSpotlight.youtubeId}?rel=0&modestbranding=1`}
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>
              <h3
                className={`order-2 lg:order-none lg:row-start-1 text-xl sm:text-2xl font-headline font-bold text-primary min-w-0 ${
                  language === 'en' ? 'lg:col-start-1' : 'lg:col-start-2'
                }`}
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              >
                {f.mediaSpotlight.linkHeading}
              </h3>
              <ul
                className={`order-3 lg:order-none lg:row-start-2 space-y-2 min-w-0 list-none p-0 m-0 ${
                  language === 'en' ? 'lg:col-start-1' : 'lg:col-start-2'
                }`}
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              >
                {f.mediaSpotlight.links.map((link) => (
                  <li key={link.href + link.label}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 rounded-2xl bg-surface-container-low border border-outline-variant/30 p-4 hover:border-secondary/50 hover:shadow-md transition-all"
                    >
                      <span className="shrink-0 w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                        <span
                          className="material-symbols-outlined text-secondary text-xl"
                          aria-hidden
                        >
                          open_in_new
                        </span>
                      </span>
                      <span className="text-base font-semibold text-on-surface group-hover:text-primary">
                        {link.label}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </section>

        {/* Current Vision — Three ventures */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <div className="text-center max-w-4xl mx-auto mb-16 space-y-4">
              <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                {language === 'ar' ? 'الخطوة التالية' : 'What\'s next'}
              </span>
              <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-primary tracking-tight">
                {f.vision.title}
              </h2>
              <p className="text-on-surface-variant text-lg leading-relaxed">{f.vision.subtitle}</p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {f.vision.items.map((item, idx) => (
              <FadeIn key={item.title} delay={idx * 80}>
                <article className="group relative h-full rounded-3xl bg-surface-container-low p-8 border border-outline-variant/30 hover:border-secondary/50 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                  <div className="absolute top-0 right-0 rtl:left-0 rtl:right-auto w-32 h-32 bg-secondary/10 rounded-bl-[3rem] rtl:rounded-br-[3rem] rtl:rounded-bl-none flex items-start justify-end rtl:justify-start p-4">
                    <span className="text-3xl font-headline font-extrabold text-secondary/80">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="relative w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-6 shadow-lg shadow-primary/20">
                    <span className="material-symbols-outlined text-secondary text-3xl" aria-hidden="true">
                      {item.icon}
                    </span>
                  </div>
                  <h3 className="relative text-2xl font-headline font-bold text-primary mb-3 pr-10 rtl:pl-10 rtl:pr-0">
                    {item.title}
                  </h3>
                  <p className="relative text-on-surface-variant leading-relaxed">{item.desc}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </section>

        {/* Work Philosophy Quote */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
          <FadeIn>
            <div className="relative rounded-[2.5rem] overflow-hidden isolate">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=80"
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/85 to-primary/70" />
              <div className="relative px-8 sm:px-16 py-16 sm:py-24 text-center max-w-4xl mx-auto space-y-6">
                <span className="material-symbols-outlined text-secondary text-6xl" aria-hidden="true">
                  format_quote
                </span>
                <span className="block text-sm font-bold uppercase tracking-[0.3em] text-secondary">
                  {f.philosophy.title}
                </span>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-headline font-semibold text-white leading-[1.4] max-w-3xl mx-auto">
                  {f.philosophy.quote}
                </p>
                <div className="pt-6">
                  <span className="inline-flex items-center gap-3 text-white/80">
                    <span className="h-px w-12 bg-secondary" aria-hidden />
                    <span className="font-bold">{f.name}</span>
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* Closing CTA */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
          <FadeIn>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="/#registration"
                className="px-8 py-4 bg-gradient-to-r from-secondary to-tertiary text-white rounded-xl font-bold text-lg border border-secondary/60 shadow-lg shadow-secondary/25 hover:shadow-[0_0_40px_-10px_rgba(239,193,28,0.6)] hover:scale-[1.02] transition-all"
              >
                {f.cta.primary}
              </a>
              <a
                href="/"
                className="px-8 py-4 bg-surface-container-low border border-outline-variant/40 text-primary rounded-xl font-bold text-lg hover:bg-surface-container transition-all"
              >
                {f.cta.secondary}
              </a>
            </div>
          </FadeIn>
        </section>
      </main>
    </>
  );
}
